import React, { memo } from 'react'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { IShipmentCommissionTierBands, Maybe } from 'types/graphqlTypes'
import DISPLAY from 'constants/display'
import CommissionTiersInfoTooltip, {
  ICommissionTiersInfoTooltipProps,
} from './CommissionTiersInfoTooltip'
import { getTierRangeLabel, getActiveCommissionTier, getAmountToNextTier } from './utils'

const useCommissionTiersSectionStyles = makeStyles(theme => ({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  commission: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  activeTier: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
}))

export interface ICommissionTiersSectionProps
  extends Pick<ICommissionTiersInfoTooltipProps, 'loading' | 'rate'> {
  /**
   * @default {}
   */
  commissionTierBands?: Maybe<IShipmentCommissionTierBands>
}

const CommissionTiersSection = memo<ICommissionTiersSectionProps>(
  ({ commissionTierBands, loading, rate }) => {
    const { tier1Max, tier2Max, tier3Max } = commissionTierBands ?? {}
    const classes = useCommissionTiersSectionStyles()

    const activeCommissionTier = getActiveCommissionTier({ rate, tier1Max, tier2Max, tier3Max })
    const activeTierRangeLabel = getTierRangeLabel({
      tierNumber: activeCommissionTier,
      tier1Max,
      tier2Max,
      tier3Max,
    })
    const amountToNextTier = getAmountToNextTier({
      rate,
      tier1Max,
      tier2Max,
      tier3Max,
    })

    return (
      <div className={classes.section}>
        <div className={classes.title}>
          <Typography variant='overline' component='div' color='textSecondary'>
            Commission
          </Typography>
          <CommissionTiersInfoTooltip
            rate={rate}
            tier1Max={tier1Max}
            tier2Max={tier2Max}
            tier3Max={tier3Max}
            loading={loading}
          />
        </div>
        {loading && (
          <div className={classes.commission}>
            <div className={classes.activeTier}>
              <Skeleton variant='text' width={50} />
              <Skeleton variant='text' width={100} />
            </div>
            <div>
              <Skeleton variant='text' width={180} />
            </div>
          </div>
        )}
        {!loading && !activeCommissionTier && <div>{DISPLAY.empty}</div>}
        {!loading && activeCommissionTier && (
          <div className={classes.commission}>
            <div className={classes.activeTier}>
              <Typography variant='subtitle2' component='div'>
                Tier {activeCommissionTier}
              </Typography>
              <Typography variant='body2' component='div' color='textSecondary'>
                ({activeTierRangeLabel})
              </Typography>
            </div>
            {amountToNextTier && (
              <div>
                <Typography variant='body2' component='div'>
                  {amountToNextTier}
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
)

export default CommissionTiersSection
